import { useCallback, useEffect, useRef, useState } from "react";
import { announce } from "@/lib/announce";

export const ANTICHAMBRE_KEY_PREFIX = "qorum.antichambre.current";
const TTL_MS = 24 * 60 * 60 * 1000;
const SCHEMA_VERSION = 2;

export const buildStorageKey = (userId: string | null | undefined) =>
  `${ANTICHAMBRE_KEY_PREFIX}::${userId ?? "anon"}`;

/** @deprecated kept for older tests / migration */
export const ANTICHAMBRE_STORAGE_KEY = buildStorageKey(null);

export type MaieuteQuestion = { id: string; text: string };

export type AntichambreState = {
  v: number;
  sessionId: string | null;
  question: string;
  mode: string;
  questions: MaieuteQuestion[];
  answers: Record<string, string>;
  currentIndex: number;
  cycleComplete: boolean;
  updatedAt: number;
};

export const EMPTY_STATE: AntichambreState = {
  v: SCHEMA_VERSION,
  sessionId: null,
  question: "",
  mode: "",
  questions: [],
  answers: {},
  currentIndex: 0,
  cycleComplete: false,
  updatedAt: 0,
};

function readStorage(userId: string | null | undefined): AntichambreState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(buildStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AntichambreState;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.v !== SCHEMA_VERSION) {
      window.localStorage.removeItem(buildStorageKey(userId));
      return null;
    }
    if (!parsed.updatedAt || Date.now() - parsed.updatedAt > TTL_MS) {
      window.localStorage.removeItem(buildStorageKey(userId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useAntichambreSession(initial: {
  question: string;
  mode: string;
  userId?: string | null;
}) {
  const userId = initial.userId ?? null;
  const [state, setState] = useState<AntichambreState>(() => {
    const stored = readStorage(userId);
    const hasSearchQuestion = !!initial.question;
    // L'URL prime : si question diffère OU recap déjà atteint → on repart à zéro.
    if (
      hasSearchQuestion &&
      stored &&
      (stored.question !== initial.question || stored.cycleComplete)
    ) {
      try {
        window.localStorage.removeItem(buildStorageKey(userId));
      } catch {
        /* ignore */
      }
      return {
        ...EMPTY_STATE,
        question: initial.question,
        mode: initial.mode,
        updatedAt: Date.now(),
      };
    }
    if (stored) return stored;
    return {
      ...EMPTY_STATE,
      question: initial.question,
      mode: initial.mode,
      updatedAt: Date.now(),
    };
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(buildStorageKey(userId), JSON.stringify(state));
    } catch {
      /* quota — ignore */
    }
  }, [state, userId]);

  // Announce step changes for screen readers
  const prevIndexRef = useRef<number>(state.currentIndex);
  useEffect(() => {
    if (prevIndexRef.current === state.currentIndex) return;
    prevIndexRef.current = state.currentIndex;
    const total = Math.max(state.questions.length, state.currentIndex + 1);
    announce(`Question ${state.currentIndex + 1} sur ${total}`, "polite");
  }, [state.currentIndex, state.questions.length]);

  const setSessionId = useCallback((id: string | null) => {
    setState((s) => (s.sessionId === id ? s : { ...s, sessionId: id, updatedAt: Date.now() }));
  }, []);

  const addQuestion = useCallback((q: MaieuteQuestion) => {
    setState((s) => {
      if (s.questions.some((existing) => existing.id === q.id)) return s;
      return { ...s, questions: [...s.questions, q], updatedAt: Date.now() };
    });
  }, []);

  const setAnswer = useCallback((questionId: string, value: string) => {
    setState((s) => ({
      ...s,
      answers: { ...s.answers, [questionId]: value },
      updatedAt: Date.now(),
    }));
  }, []);

  const advance = useCallback(() => {
    setState((s) => ({ ...s, currentIndex: s.currentIndex + 1, updatedAt: Date.now() }));
  }, []);

  const goBack = useCallback(() => {
    setState((s) => ({
      ...s,
      currentIndex: Math.max(0, s.currentIndex - 1),
      cycleComplete: false,
      updatedAt: Date.now(),
    }));
  }, []);

  const setCycleComplete = useCallback((complete: boolean) => {
    setState((s) => ({ ...s, cycleComplete: complete, updatedAt: Date.now() }));
  }, []);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(buildStorageKey(userId));
    } catch {
      /* ignore */
    }
    setState({
      ...EMPTY_STATE,
      question: initial.question,
      mode: initial.mode,
      updatedAt: Date.now(),
    });
  }, [initial.question, initial.mode, userId]);

  return {
    state,
    setSessionId,
    addQuestion,
    setAnswer,
    advance,
    goBack,
    setCycleComplete,
    clear,
  };
}

export const ANTICHAMBRE_TTL_MS = TTL_MS;

/** Purge toutes les clés antichambre (toutes scopes user). */
export function purgeAllAntichambreStorage() {
  if (typeof window === "undefined") return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(`${ANTICHAMBRE_KEY_PREFIX}::`)) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
